import { css } from '@emotion/react';
import React, { useEffect, useRef, useState } from 'react';
import { AiProfileInfo, OnboardingAiProfileType } from '@/customTypes/chapter';
import { AiCharacterImageType, MainCharacter } from '@/customTypes/storygame';
import { SPLoading } from '@/components/commons';
import theme from '@/styles/theme';
import StoryGamePromotion from './StoryGamePromotion';
import { IStoryGameOnboardingAlert } from '@/components/storyGame/popup/StoryGameOnboardingAlert';
import StoryGameAiImageButton from '@/components/commons/popup/StoryGameAiImageButton';
import StoryGameMyImageDefaultBubble from '@/components/commons/popup/StoryGameMyImageDefaultBubble';
import Image from 'next/image';
import {
  StoryGameNameEdit,
  StoryGamePromotionImg,
  StoryGamePromotionImg2,
  StoryGamePromotionImg3,
} from 'public/assets';
import useImagePreloader from '@/hooks/useImagePreloader';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import MobileStoryGameCharacterList from './MobileStoryGameCharacterList';
import SpeechBubble from '@/components/commons/popup/StoryGameSpeechBubble';
import MobileStoryGameCharacterNameInput from './MobileStoryGameCharacterNameInput';
import { isMobileDevice } from '@/common/utils';
import { useHoverTouch } from '@/hooks/useHoverTouch';
import useTranslation from 'next-translate/useTranslation';

interface MobileStoryGameAiImageSelectPopupProps {
  setAiProfileType: (type: OnboardingAiProfileType) => void;
  onNext: () => void;
  onChangeAiProfile: (newAiProfileInfo: AiProfileInfo) => void;
  onGetAiCharacterSessionId: () => Promise<void>;

  onGetAiCharacterImageList: (chrId?: number) => Promise<void>;
  aiCharacterImageList?: AiCharacterImageType[]; //profileImage는 default캐릭터에만 있음
  onClickStoryGameCharacter: (character: AiProfileInfo) => void;
  selectedCharacter?: MainCharacter;
  onCreateCharacter: () => Promise<void>;
  aiProfileInfo?: AiProfileInfo;
  aiProfileInfoArray?: AiProfileInfo[];
  addOpenedStorygameIds: () => void;
  openedStorygameIds?: string;
  isShowPromotionStorygame: boolean;
  setIsShowPromotionStorygame: (value: boolean) => void;
  bg?: string;
  selectPreviousCharacter: () => void;
  selectNextCharacter: () => void;
  storygameAlertData: IStoryGameOnboardingAlert;
  setStorygameAlertData: (data: IStoryGameOnboardingAlert) => void;
  isNameMissingForSave: () => boolean;
  onClickMyImage: (character: AiCharacterImageType) => void;
}

const ToggleButton = (props: {
  setIsHidden: React.Dispatch<React.SetStateAction<boolean>>;
  isHidden: boolean;
}) => {
  const { isHidden, setIsHidden } = props;
  return (
    <button
      css={toggleButtonStyle}
      onClick={() => {
        setIsHidden(!isHidden);
      }}
    >
      {isHidden ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M4 12L10 6L16 12"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M16 7L10 13L4 7"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
};

const AiCharacterImageComponent = ({
  aiProfileInfo,
  character,
  onClickMyImage,
}: {
  aiProfileInfo: AiProfileInfo;
  character: AiCharacterImageType;
  onClickMyImage: (character: AiCharacterImageType) => void;
}) => {
  const { isHovered, hoverProps } = useHoverTouch();
  const { t } = useTranslation();

  const isSelected = aiProfileInfo?.requestId === character.id;
  return (
    <div style={{ position: 'relative' }}>
      <div
        className={`ai-character-section ${isSelected ? 'selected' : ''}`}
        onClick={() => onClickMyImage(character)}
      >
        {character.imageFile?.link ? (
          <Image
            src={character.imageFile?.link}
            layout="fill"
            className="ai-character-image"
            alt="char"
          />
        ) : (
          <div className="ai-character-image">
            <SPLoading isFixed size="2rem" />
          </div>
        )}
        {isSelected && (
          <div className="overlay-bg">
            <div className="status-info">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M2.5 9.5L7.82258 15L17.5 5"
                  stroke="white"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        )}

        {character.isDefault && (
          <span
            className="info-icon"
            onMouseEnter={hoverProps.onMouseEnter}
            onMouseLeave={hoverProps.onMouseLeave}
            onTouchStart={hoverProps.onTouchStart}
            onTouchEnd={hoverProps.onTouchEnd}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <rect
                x="0.75"
                y="0.75"
                width="18.5"
                height="18.5"
                rx="9.25"
                stroke="white"
                strokeOpacity="0.75"
                strokeWidth="1.5"
              />
              <rect
                x="9"
                y="7.5"
                width="2"
                height="8"
                rx="1"
                fill="white"
                fillOpacity="0.75"
              />
              <rect
                x="9"
                y="4.5"
                width="2"
                height="2"
                rx="1"
                fill="white"
                fillOpacity="0.75"
              />
            </svg>
          </span>
        )}
      </div>
      {isHovered && character.isDefault ? (
        <div
          style={{
            position: 'absolute',
            top: 35,
            width: '130%', // 120%에서 130%로 변경
            left: '50%', // 추가: 왼쪽 기준점을 부모의 중앙으로 이동
            transform: 'translate(-50%)', // 추가: 자신의 너비의 절반만큼 왼쪽으로 이동
          }}
        >
          <StoryGameMyImageDefaultBubble trianglePosition={'top'}>
            {t(
              'storygame_ai_onboarding_screen_description_preset_image_by_the_author',
            )}
          </StoryGameMyImageDefaultBubble>
        </div>
      ) : null}
    </div>
  );
};

const MobileStoryGameAiImageSelectPopup = ({
  setAiProfileType,
  aiCharacterImageList,

  onClickStoryGameCharacter,
  selectedCharacter,
  onCreateCharacter,
  aiProfileInfo,
  aiProfileInfoArray,
  isShowPromotionStorygame,
  setIsShowPromotionStorygame,
  bg,
  onClickMyImage,
  isNameMissingForSave,
  onChangeAiProfile,
}: MobileStoryGameAiImageSelectPopupProps) => {
  const { t } = useTranslation();
  const { isHovered, hoverProps } = useHoverTouch();
  const [isShowGuideBubble] = useState(isShowPromotionStorygame);
  const [isShowNameInput, setIsShowNameInput] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const [fullName, setFullName] = useState('');

  const textRef = useRef<HTMLHeadingElement>(null);

  const images = [
    StoryGamePromotionImg,
    StoryGamePromotionImg2,
    StoryGamePromotionImg3,
  ];
  const imagesPreloaded = useImagePreloader(images);

  useUnsavedChangesWarning(true);

  useEffect(() => {
    setFullName(
      aiProfileInfo?.lastName || aiProfileInfo?.firstName
        ? aiProfileInfo?.lastName + '' + aiProfileInfo?.firstName
        : t('storygame_ai_onboarding_screen_description_character_name'),
    );
  }, [aiProfileInfo?.lastName, aiProfileInfo?.firstName]);

  return (
    <div css={styles(isMobileDevice())}>
      <div className="bg-image" />
      <div className={`bg${isShowPromotionStorygame ? '-promotion' : ''}`} />
      <div className="top-container">
        <div className="top-title">
          {isShowPromotionStorygame
            ? ''
            : t('storygame_ai_onboarding_screen_title_character_setup')}
        </div>
        {isShowPromotionStorygame ? null : (
          <div
            className={
              isNameMissingForSave() ? 'top-right' : 'top-right-disabled'
            }
            onClick={() => onCreateCharacter()}
          >
            {t('storygame_ai_onboarding_screen_button_save')}
          </div>
        )}
      </div>

      <div
        className="container"
        style={isShowPromotionStorygame ? { display: 'none' } : {}}
      >
        <div className="character-list-setup">
          <MobileStoryGameCharacterList
            characterList={aiProfileInfoArray}
            selectedCharacter={selectedCharacter}
            onClickStoryGameCharacter={onClickStoryGameCharacter}
            aiCharacterImageList={aiCharacterImageList}
            onProfileEdit={function (): void {
              setAiProfileType('ProfileCrop');
            }}
            // isDefault={
            //   aiCharacterImageList?.find((characterImage) => {
            //     return (
            //       characterImage.profileImage?.fileId ===
            //       aiProfileInfo?.defaultProfileImageFile?.fileId
            //     );
            //   })?.isDefault ?? true
            // }
          />
        </div>

        <div className="name-container">
          <div
            className={`name-text${
              aiProfileInfo?.lastName || aiProfileInfo?.firstName
                ? ''
                : ' empty'
            }`}
          >
            <div ref={textRef} style={{ position: 'relative' }}>
              {fullName}
              <StoryGameNameEdit
                onClick={() => {
                  setIsShowNameInput(true);
                }}
                style={{
                  position: 'absolute',
                  right: -5,
                  top: '50%',
                  transform: 'translate(100%, -50%)',
                  cursor: 'pointer',
                }}
              />
            </div>
          </div>
          <span className="display-name-text">
            {selectedCharacter?.displayName}
          </span>
        </div>
        <div className="image-container">
          {!isShowPromotionStorygame && (
            <>
              {aiProfileInfo?.portraitFile?.link ? (
                <Image
                  src={aiProfileInfo?.portraitFile?.link}
                  className="ai_character"
                  alt="image"
                  layout="fill"
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <div css={loadingCss('4rem')}>
                    <div className="loading" />

                    <div className="loading-text1">
                      {t(
                        'storygame_ai_onboarding_screen_description_generating',
                      )}
                    </div>
                    <div className="loading-text2">
                      {t(
                        'storygame_ai_onboarding_screen_description_estimated_time',
                        { value: 1 },
                      )}
                    </div>
                    <div className="loading-text3">
                      {t(
                        'storygame_ai_onboarding_screen_description_know_this_warning',
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div className="bottom-container"></div>
        </div>
        <div
          className="side-container-bottom"
          style={{ opacity: isShowPromotionStorygame ? 0.7 : 1 }}
        >
          <div css={aiCharacterSetupStyle(isHidden)}>
            <span
              css={css`
                position: absolute;
                top: -40px;
                left: 0;
                color: #333;
                border: none;
                padding: 5px 10px;
                border-radius: 5px;
                cursor: pointer;
                z-index: 10;
              `}
              onMouseEnter={hoverProps.onMouseEnter}
              onMouseLeave={hoverProps.onMouseLeave}
              onTouchStart={hoverProps.onTouchStart}
              onTouchEnd={hoverProps.onTouchEnd}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <rect
                  x="0.75"
                  y="0.75"
                  width="18.5"
                  height="18.5"
                  rx="9.25"
                  stroke="white"
                  strokeOpacity="0.75"
                  strokeWidth="1.5"
                />
                <rect
                  x="9"
                  y="7.5"
                  width="2"
                  height="8"
                  rx="1"
                  fill="white"
                  fillOpacity="0.75"
                />
                <rect
                  x="9"
                  y="4.5"
                  width="2"
                  height="2"
                  rx="1"
                  fill="white"
                  fillOpacity="0.75"
                />
              </svg>
            </span>
            <div
              style={{
                position: 'absolute',
                top: -92,
                left: -5,
              }}
            >
              {isHovered && (
                <StoryGameMyImageDefaultBubble
                  triangleHorizontalPosition="left"
                  trianglePosition="bottom"
                  textAlign="left"
                >
                  {t('storygame_ai_onboarding_screen_description_report_nasty')}
                </StoryGameMyImageDefaultBubble>
              )}
            </div>
            <ToggleButton setIsHidden={setIsHidden} isHidden={isHidden} />
            {/* <h2 className="title">내 이미지</h2> */}

            <div className="ai-character-list-container">
              <div className="ai-character-list">
                <div
                  className="ai-image-section"
                  onClick={() => {
                    // addOpenedStorygameIds();
                    if (aiProfileInfo?.cropImageFile) {
                      setAiProfileType('AiImageFilter');
                      return;
                    }
                    setAiProfileType('AiImageUpload');
                  }}
                >
                  <StoryGameAiImageButton />
                  {isShowGuideBubble && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 'calc(-100% - 10px)',
                      }}
                    >
                      <SpeechBubble />
                    </div>
                  )}
                </div>
                <div className="divider"></div>
                {aiProfileInfo &&
                  aiCharacterImageList?.map((character) => {
                    return (
                      <AiCharacterImageComponent
                        key={character.id}
                        character={character}
                        aiProfileInfo={aiProfileInfo}
                        onClickMyImage={onClickMyImage}
                      />
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isShowNameInput && selectedCharacter && (
        <MobileStoryGameCharacterNameInput
          characterImage={
            aiProfileInfo?.profileFile ??
            aiProfileInfo?.defaultProfileImageFile?.link
          }
          firstName={aiProfileInfo?.firstName ?? ''}
          lastName={aiProfileInfo?.lastName ?? ''}
          placeholderLastName={selectedCharacter.lastName}
          placeholderFirstName={selectedCharacter.firstName}
          displayName={selectedCharacter.displayName}
          onChangeAiProfile={onChangeAiProfile}
          onProfileEdit={function (): void {
            setAiProfileType('ProfileCrop');
          }}
          isError={aiProfileInfo?.isError}
          onClose={() => setIsShowNameInput(false)}
        />
      )}

      {isShowPromotionStorygame && imagesPreloaded && (
        <StoryGamePromotion
          setIsShowPromotionStorygame={setIsShowPromotionStorygame}
        />
      )}

      {/* <div style={{ position: 'absolute', zIndex: 10 }}>
        <StoryGameHtmlDownload htmlContent={testHtml} />
      </div> */}
    </div>
  );
};

const baseButtonStyle = `
  position: absolute;
  right: 12px;
  display: flex;
  height: 40px;
  min-height: 40px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  
  @media screen and (max-width: ${theme.devices.md}px) {
    font-size: 14px;
  }
`;

const styles = (isMobileDevice: boolean) => css`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  // top: env(safe-area-inset-top);

  .name-container {
    display: flex;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    padding: 0px 8px 12px;
  }
  .name-text {
    margin: 24px 0px 2px 0px;
    position: relative;
    display: flex;
    justify-content: center;
    color: #fff;
    text-align: center;
    text-shadow: 0px 0px 8px rgba(0, 0, 0, 0.1);
    font-size: 20px;
    font-weight: 500;
    line-height: 26px; /* 130% */
  }
  .name-text.empty {
    color: #ffffff80;
  }

  .display-name-text {
    color: rgba(255, 255, 255, 0.75);
    text-align: center;

    font-size: 14px;
    line-height: 20px; /* 142.857% */
  }
  .bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #00000040;
    backdrop-filter: blur(8px);
  }
  .bg-promotion {
    background-color: #000000bf;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    backdrop-filter: blur(8px);
  }
  .top-container {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    padding: 12px;
    height: ${isMobileDevice ? '48px' : '56px'};
    justify-content: center;

    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    z-index: 1;

    @media screen and (max-width: ${theme.devices.md}px) {
      padding: 0px;
      border-bottom: 0px;
      min-height: 48px;
    }
  }

  .top-container .top-right {
    ${baseButtonStyle}
    color: #fff;
    background: #753bbd;
  }

  .top-container .top-right-disabled {
    ${baseButtonStyle}
    color: var(--whiteA25, rgba(255, 255, 255, 0.25));
    background: var(--whiteA10, rgba(255, 255, 255, 0.1));
    cursor: not-allowed;
  }

  .top-container .top-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    position: absolute;
  }

  .container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  .image-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center; /* 세로 중앙 정렬 */
    overflow: hidden;
    position: relative;
  }

  .image-container .ai_character {
    width: auto !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: contain !important; /* cover 대신 contain 사용 */
    object-position: center center !important; /* 중앙 정렬 */
    position: relative !important;
    left: 50% !important; /* 가로 중앙 정렬을 위해 추가 */
    transform: translateX(-50%) !important; /* 가로 중앙 정렬을 위해 추가 */
  }

  .bottom-container {
    position: absolute;
    bottom: 10%;
  }

  .side-container {
    display: flex;
    flex-direction: column;

    margin: 20px 0px;
    align-items: center;
    justify-content: center;

    padding: 0px 0px 0px 48px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    flex-direction: column;
    z-index: 1;
  }
  .character-list-setup {
    color: white;
    display: flex;
    flex-direction: column;
  }

  .side-container-bottom {
    display: flex;
    flex-direction: column;
    position: absolute;
    bottom: 0px;
    width: 100%;
    align-items: center;
    justify-content: center;

    padding: 0px 16px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    flex-direction: column;

    height: 11.3rem;
    margin: 0px 0px calc(env(safe-area-inset-top) + 16px) 0px;
  }

  .ai-character-setup {
    position: relative;
    padding: 16px 0px;
    color: white;
    display: flex;
    flex-direction: column;
    width: 100%;
    border-radius: 12px;
    border-radius: 8px;
    border: var(--letter-spacing-en, 1px) solid rgba(255, 255, 255, 0.25);
    background: var(--blackA50, rgba(0, 0, 0, 0.5));

    backdrop-filter: blur(8px);

    transition: transform 0.3s ease-in-out;
  }

  .title {
    text-align: center;
    margin-bottom: 14px;
    color: #fff;

    font-size: 18px;
    font-weight: 500;
  }

  .ai-image-section {
    width: 88px;
    height: 88px;
    border-radius: 8px;

    text-align: center;

    font-size: 14px;
    justify-content: center;
    align-items: center;
    display: flex;
    cursor: pointer;
  }

  .ai-icon {
    width: 24px;
    height: 24px;
    font-size: 24px;
    margin-bottom: 10px;
  }

  .ai-text {
    margin: 0;
  }
  .ai-character-list-container {
    position: relative;
    width: 100%;
    overflow-x: scroll;
    display: flex;
    flex-direction: row;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    // padding: 0px 20px;
  }
  .ai-character-list {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }

  .ai-character-section {
    width: 88px;
    height: 88px;
    flex-shrink: 0;
    border-radius: 10px;
    border: solid 1px transparent;
    display: flex;
    align-items: center;
    position: relative;
    justify-content: center;
    cursor: pointer;
    transition: border-color 0.3s ease;
    border-color: #ffffff80;
    opacity: 0.75;
  }

  .ai-character-section.selected {
    border-color: #9457eb;
    opacity: 1;

    overflow: hidden;
  }

  .ai-character-image {
    width: 100%;
    height: 100%;
    border-radius: 5px;
    object-fit: contain;
  }
  .overlay-bg {
    width: 100%;
    height: 100%;
    background-color: #9457eb40;
    position: absolute;
    top: 0px;
    left: 0px;
  }

  .status-info {
    position: absolute;
    top: 0px;
    left: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #9457eb;
    border-radius: 8px 0px 8px 0px;
    width: 32px;
    height: 32px;
  }

  .ai-character-status {
    margin: 0;
    font-size: 13px;
    color: #ddd;
  }

  .info-icon {
    font-size: 16px;
    position: absolute;
    top: 6px;
    right: 6px;
    color: #ffffffbf;
  }

  .loading-text1 {
    margin-top: 5px;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
    color: #ffffff;
  }
  .loading-text2 {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
    color: #ffffffbf;
  }
  .loading-text3 {
    display: flex;
    padding: 8px 12px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: var(--whiteA50, rgba(255, 255, 255, 0.5));
    text-align: center;
    font-size: 12px;
    line-height: 16px; /* 133.333% */
    margin-top: 12px;
  }

  .divider {
    min-width: 1px;
    background: var(--whiteA25, rgba(255, 255, 255, 0.25));
    margin: 8px;
  }

  .absolute-element {
    position: absolute;
    top: 10px;
    right: -50px;
    width: 100px;
    height: 50px;
    background-color: rgba(255, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
  }

  // @media screen and (max-width: 1024px) {
  //   width: 1024px;
  // }
`;

const loadingCss = (size: string) => css`
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-bottom: 11.3rem;
  .loading {
    width: ${size ? size : '8em'};
    height: ${size ? size : '8em'};
    border-radius: 50%;
    border: 5px solid ${theme.colors.primary500};
    border-top: 3px solid transparent;
    animation: spin 1s linear infinite;
    @media screen and (max-width: ${theme.devices.mobile}) {
      width: ${size ? size : '4em'};
      height: ${size ? size : '4em'};
      border-width: 3px;
    }
  }
`;

const toggleButtonStyle = css`
  position: absolute;
  top: -48px;
  right: 0;
  background-color: #fff;
  color: #333;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;
  z-index: 10;

  border-radius: 100px;
  background: var(--blackA25, rgba(0, 0, 0, 0.25));

  /* backgroundBlur8 */
  backdrop-filter: blur(8px);
  width: 40px;
  height: 40px;
  padding: 10.998px 10px 9.002px 10px;
`;

const aiCharacterSetupStyle = (isHidden: boolean) => css`
  position: relative;
  padding: 16px;
  color: white;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  transition: transform 0.3s ease-in-out;
  transform: ${isHidden ? 'translateY(calc(100%))' : 'translateY(0)'};
`;

export default MobileStoryGameAiImageSelectPopup;
